"use client";
import {
  AppRoutesEnum,
  CookiesEnum,
  cookiesValues,
  GAMIFICATION_CLAIM_TIME_EXPIRY_SECONDS,
  PageNameEnum,
  PaywallPageCookieEnum,
  SUBSCRIPTION_STATUS,
  TV_SIDE_NAVIGATION,
  URLQueryParamsEnum,
} from "@/constants/constants";
import { useUserContext } from "@/context/UserProvider";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useDeviceType } from "@/hooks/useDeviceType";
import { useContinueWatchingWidget } from "@/hooks/useContinueWatching";
import { HomeFooter } from "@/screens/home/components/HomeFooter";
import { getClientSideCookieValue, isTvEnv } from "@/utils/common.helpers";
import {
  fetchAndSetLocationInLocalStorage,
  isInternationalCountry,
} from "@/utils/location.helpers";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import AppDownloadBanner from "./AppDownloadBanner";
import { BottomNavigation } from "./BottomNavigation";
import { Header, HeaderProps } from "./Header";
import { UserCulturePopup } from "./userCulture/UserCulturePopup";
import { TvSideNavigation } from "./TvSideNavigation";
import StartTrialBanner from "./StartTrialBanner";
import { AppOpenDrawer } from "./AppOpenDrawer";
import { ExpiresInTimer } from "./ExpiresInTimer";
import SpecialAccessBanner from "./SpecialAccessBanner";
import { SpecialAccessDrawer } from "./SpecialAccessDrawer";
import { useSpecialAccess } from "@/hooks/useSpecialAccess";
import { useSpecialAccessContext } from "@/context/SpecialAccessProvider";
import { ConsumptionStateCategoryEnum } from "@/screens/specialAccess/types/specialAccess.types";
import { LocationChangePopup } from "./LocationChangePopup";

/** Header element height in px — used to collapse sticky header space on full-bleed pages */
const LAYOUT_HEADER_HEIGHT = 76;

export enum PathNames {
  COMMON_HOME = "COMMON_HOME",
  COMMON_DETAILED_ROW = "COMMON_DETAILED_ROW",
  SELECT_CULTURE = "SELECT_CULTURE",
  HOME = "HOME",
  SEARCH = "SEARCH",
  GENRE = "GENRE",
  DETAILED_ROW = "DETAILED_ROW",
  MOVIES = "MOVIES",
  SHOWS = "SHOWS",
  MY_LIST = "MY_LIST",
  SETTINGS = "SETTINGS",
  CHECKOUT = "CHECKOUT",
  MOVIE_DETAIL = "MOVIE_DETAIL",
  SHOW_DETAIL = "SHOW_DETAIL",
  MOVIE_CHARACTERS = "MOVIE_CHARACTERS",
  SHOW_CHARACTERS = "SHOW_CHARACTERS",
  TITLE = "TITLE",
  MOVIE_WATCH = "MOVIE_WATCH",
  SHOW_WATCH = "SHOW_WATCH",
}

interface Config {
  showHeader: boolean;
  headerProps?: HeaderProps;
  showFooter?: boolean;
  mobileOnly?: {
    showStartTrialBanner?: boolean;
    showSpecialAccessBanner?: boolean;
    showBottomNavigation?: boolean;
    showBottomDownloadBanner?: boolean; // Added flag for AppDownloadBanner
    showAppOpenDrawer?: boolean;
    forceShowBottomDownloadBanner?: boolean;
    showSpecialAccessDrawer?: boolean;
  };
  userCulturePopup?: {
    show?: boolean;
    allowAutoSelectedFromURL?: boolean;
    showOnlyForLoggedInUser?: boolean;
  };
  showTvSideNavigation?: boolean;
  showClaimTimer?: boolean;
  pageName?: PageNameEnum;
  transparentFooter?: boolean;
}

export const LayoutCSR = ({
  lang,
  dialect,
  children,
}: {
  lang: string;
  dialect: string;
  children: React.ReactNode;
}) => {
  const pathName = usePathname();
  const { isMobile } = useDeviceType();
  const {
    subscriptionStatus,
    selectedProfile,
    isLoggedIn,
    isSubscribed,
    hasActiveAccess,
    isInternational,
    setIsInternational,
  } = useUserContext();
  const { clearContinueWatchingData } = useContinueWatchingWidget();
  const { t } = useClientTranslations();
  const [userCulture, setUserCulture] = useState<string>();
  const [tempCulture, setTempCulture] = useState<string>();
  const previousProfileIdRef = useRef<string | undefined>(
    selectedProfile?.profileId,
  );
  const mobileBottomStackRef = useRef<HTMLDivElement>(null);
  const searchParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : undefined;
  const showUserCulturePopupParam =
    searchParams?.get(URLQueryParamsEnum.SHOW_USER_CULTURE_POPUP) !== "false";
  const shouldForceUserCulturePopup =
    searchParams?.get(URLQueryParamsEnum.SHOW_USER_CULTURE_POPUP) === "true";

  useEffect(() => {
    const root = document.documentElement;
    const stackElement = mobileBottomStackRef.current;
    const navElement = document.querySelector<HTMLElement>(
      '[data-testid="bottom-navigation"]',
    );

    const updateBottomStackHeight = () => {
      const stackHeight = stackElement?.offsetHeight ?? 0;
      const navHeight = navElement?.offsetHeight ?? 0;
      root.style.setProperty(
        "--mobile-bottom-overlay-height",
        `${stackHeight + navHeight}px`,
      );
    };

    updateBottomStackHeight();

    const resizeObserver = new ResizeObserver(updateBottomStackHeight);
    if (stackElement) {
      resizeObserver.observe(stackElement);
    }
    if (navElement) {
      resizeObserver.observe(navElement);
    }
    window.addEventListener("resize", updateBottomStackHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateBottomStackHeight);
      root.style.removeProperty("--mobile-bottom-overlay-height");
    };
  }, [pathName]);
  const [isAppOpenDrawerOpen, setIsAppOpenDrawerOpen] = useState<boolean>(true);
  const isAppOpenDrawerOpenRef = useRef<boolean>(true);
  const paywallPageCookie = getClientSideCookieValue(
    cookiesValues[CookiesEnum.PaywallPage],
  );
  const [expiresInSeconds, setExpiresInSeconds] = useState(0);
  const [gamificationClaimTime, setGamificationClaimTime] = useState<
    string | null
  >(null);
  const [hasShownLocationPopup, setHasShownLocationPopup] = useState(false);
  const { isSpecialAccessEnabled } = useSpecialAccess();
  const { totalConsumptionStates, currentSpecialAccessData } =
    useSpecialAccessContext();

  const routePaths = {
    [PathNames.COMMON_HOME]: `/`,
    [PathNames.COMMON_DETAILED_ROW]: `/${lang}/detail/*`,
    [PathNames.SELECT_CULTURE]: `/${lang}${AppRoutesEnum.SELECT_CULTURE}`,
    [PathNames.HOME]: `/${lang}/${dialect}`,
    [PathNames.SEARCH]: `/${lang}/${dialect}/search`,
    [PathNames.GENRE]: `/${lang}/${dialect}/genre*`, // Dynamic match for GENRE: (startsWith logic)
    [PathNames.DETAILED_ROW]: `/${lang}/${dialect}/detail/*`, // Dynamic match for EXPANDED_ROW: (startsWith logic)
    [PathNames.MOVIES]: `/${lang}/${dialect}/movies`,
    [PathNames.SHOWS]: `/${lang}/${dialect}/shows`,
    [PathNames.MY_LIST]: `/${lang}/${dialect}/my-list`,
    [PathNames.SETTINGS]: `/${lang}/${dialect}/settings`,
    [PathNames.CHECKOUT]: `/${lang}/${dialect}/checkout*`,
    [PathNames.MOVIE_CHARACTERS]: `/${lang}/${dialect}/movie/*/characters*`,
    [PathNames.SHOW_CHARACTERS]: `/${lang}/${dialect}/show/*/characters*`,
    [PathNames.MOVIE_DETAIL]: `/${lang}/${dialect}/movie/*`,
    [PathNames.SHOW_DETAIL]: `/${lang}/${dialect}/show/*`,
    [PathNames.TITLE]: `/${lang}/${dialect}/title/*`,
    [PathNames.MOVIE_WATCH]: `/${lang}/${dialect}/movie/watch/*`,
    [PathNames.SHOW_WATCH]: `/${lang}/${dialect}/show/watch/*`,
  };

  useEffect(() => {
    // Check for gamification claim time
    const claimTimeValue = getClientSideCookieValue(
      cookiesValues[CookiesEnum.GamificationClaimTime],
    );

    setGamificationClaimTime(claimTimeValue || null);

    if (claimTimeValue) {
      const claimTimeLapse = new Date().getTime() - parseInt(claimTimeValue);
      const claimTimeLapseSeconds = Math.floor(claimTimeLapse / 1000);
      const expiresIn =
        GAMIFICATION_CLAIM_TIME_EXPIRY_SECONDS - claimTimeLapseSeconds;
      if (expiresIn > 0) {
        setExpiresInSeconds(expiresIn);
      } else {
        setExpiresInSeconds(0);
      }
    } else {
      setExpiresInSeconds(0);
    }
  }, [pathName]);

  const matchPath = (path: string, pattern: string): boolean => {
    // Handle patterns with wildcard in the middle AND trailing wildcard (e.g., /movie/*/characters*)
    // This must be checked BEFORE the general /*/ pattern to avoid conflicts
    if (pattern.includes("/*/") && pattern.endsWith("*")) {
      const [prefix, suffixWithWildcard] = pattern.split("/*/");
      const suffix = suffixWithWildcard.slice(0, -1); // Remove the trailing '*'

      if (path.startsWith(prefix + "/") && path.includes("/" + suffix)) {
        // Find the position of the suffix in the path
        const suffixIndex = path.indexOf("/" + suffix);
        if (suffixIndex === -1) return false;

        // Extract the part between prefix and suffix
        const middlePart = path.slice(prefix.length + 1, suffixIndex);
        // Ensure there's exactly one segment (the slug) between prefix and suffix
        return middlePart.length > 0 && !middlePart.includes("/");
      }
      return false;
    }
    // Handle patterns with wildcard in the middle (e.g., /movie/*/characters)
    if (pattern.includes("/*/")) {
      const [prefix, suffix] = pattern.split("/*/");
      if (path.startsWith(prefix + "/") && path.endsWith("/" + suffix)) {
        // Ensure there's at least one segment between prefix and suffix
        const middlePart = path.slice(prefix.length + 1, -suffix.length - 1);
        return middlePart.length > 0 && !middlePart.includes("/");
      }
      return false;
    }
    if (pattern.endsWith("*")) {
      // Handle wildcard match (startsWith logic)

      const basePattern = pattern.slice(0, -1); // Remove the '*' character
      return path.startsWith(basePattern);
    }
    // Exact match for other paths
    return path === pattern;
  };

  // Find all matching routes and return the most specific one (longest pattern)
  const currentRoute = (() => {
    const matches = Object.entries(routePaths)
      .filter(([_, pattern]) => matchPath(pathName, pattern))
      .map(([key, pattern]) => ({
        key: key as PathNames,
        pattern,
        specificity: pattern.length, // Longer pattern = more specific
      }))
      .sort((a, b) => b.specificity - a.specificity); // Sort by specificity (longest first)

    return matches.length > 0 ? matches[0].key : undefined;
  })() as PathNames | undefined;

  // FIXME: remove duplication of config
  const config: Record<PathNames, Config> = {
    [PathNames.COMMON_HOME]: {
      showHeader: true,
      headerProps: {
        settings: true,
        dialect: dialect,
        hideLogo: false,
        showSearchInput: !isTvEnv(),
        showBackIcon: false,
        newHeaderHeight: true,
        shouldShowHeaderNavigation: !isTvEnv(),
        isSticky: !isTvEnv(),
        hideBorder: true,
        hideBackground: true,
        showDialectSelector: true,
        showUserCulturePopup: true,
      },
      showFooter: false,
      mobileOnly: {
        showStartTrialBanner: true,
        showBottomNavigation: true,
        showBottomDownloadBanner: true,
        showAppOpenDrawer: hasActiveAccess,
        showSpecialAccessBanner: true,
      },
      userCulturePopup: {
        show: false,
      },
      pageName: PageNameEnum.HOME,
    },
    [PathNames.COMMON_DETAILED_ROW]: {
      showHeader: true,
      headerProps: {
        settings: true,
        dialect: dialect,
        hideLogo: isMobile,
        showSearchInput: true,
        showBackIcon: isMobile,
        showDialectSelector: true,
        newHeaderHeight: true,
        showUserCulturePopup: true,
      },
      showFooter: true,
    },
    [PathNames.SELECT_CULTURE]: {
      showHeader: false,
      userCulturePopup: {
        show: true,
      },
    },
    [PathNames.HOME]: {
      showHeader: true,
      showTvSideNavigation: true,
      headerProps: {
        settings: true,
        dialect: dialect,
        hideLogo: false,
        showSearchInput: !isTvEnv(),
        showBackIcon: false,
        newHeaderHeight: true,
        shouldShowHeaderNavigation: !isTvEnv(),
        isSticky: !isTvEnv(),
        hideBorder: true,
        hideBackground: true,
        showDialectSelector: true,
      },
      showFooter: false,
      mobileOnly: {
        showStartTrialBanner: true,
        showBottomNavigation: true,
        showBottomDownloadBanner: true,
        showAppOpenDrawer: hasActiveAccess,
        showSpecialAccessBanner: true,
      },
      userCulturePopup: {
        show: showUserCulturePopupParam,
      },
      pageName: PageNameEnum.HOME,
    },
    [PathNames.SEARCH]: {
      showHeader: true,
      showTvSideNavigation: true,
      headerProps: {
        settings: !isMobile,
        dialect: dialect,
        hideLogo: isMobile,
        showSearchInput: true,
        showBackIcon: isMobile,
        triggerBackOnSearchClear: !isMobile,
        showDialectSelector: !isMobile,
        newHeaderHeight: true,
        shouldShowHeaderNavigation: !isTvEnv(),
        hideBorder: !isMobile,
      },
      showFooter: false,
      mobileOnly: {
        showBottomNavigation: true,
        showSpecialAccessBanner: true,
      },
    },
    [PathNames.GENRE]: {
      showHeader: true,
      headerProps: {
        settings: true,
        dialect: dialect,
        hideLogo: isMobile,
        showSearchInput: true,
        showBackIcon: isMobile,
        showDialectSelector: true,
        newHeaderHeight: true,
        shouldShowHeaderNavigation: true,
      },
      showTvSideNavigation: true,
      showFooter: true,
      transparentFooter: true,
      userCulturePopup: {
        show: shouldForceUserCulturePopup,
      },
      mobileOnly: {
        showSpecialAccessBanner: true,
      },
    },
    [PathNames.DETAILED_ROW]: {
      showHeader: true,
      headerProps: {
        settings: true,
        dialect: dialect,
        hideLogo: isMobile,
        showSearchInput: true,
        showBackIcon: isMobile,
        showDialectSelector: true,
        newHeaderHeight: true,
      },
      showFooter: true,
    },
    [PathNames.MOVIES]: {
      showHeader: true,
      showTvSideNavigation: true,
      headerProps: {
        settings: true,
        dialect: dialect,
        hideLogo: false,
        showSearchInput: true,
        showBackIcon: false,
        newHeaderHeight: true,
        shouldShowHeaderNavigation: true,
        hideBorder: true,
        showDialectSelector: true,
      },
      showFooter: true,
      mobileOnly: {
        showSpecialAccessBanner: true,
      },
      userCulturePopup: {
        show: true,
      },
    },
    [PathNames.SHOWS]: {
      showHeader: true,
      showTvSideNavigation: true,
      headerProps: {
        settings: true,
        dialect: dialect,
        hideLogo: false,
        showSearchInput: true,
        showBackIcon: false,
        newHeaderHeight: true,
        shouldShowHeaderNavigation: true,
        hideBorder: true,
        showDialectSelector: true,
      },
      showFooter: true,
      mobileOnly: {
        showSpecialAccessBanner: true,
      },
      userCulturePopup: {
        show: true,
      },
    },
    [PathNames.MY_LIST]: {
      showHeader: true,
      showTvSideNavigation: true,
      headerProps: {
        settings: true,
        dialect: dialect,
        hideLogo: false,
        showSearchInput: true,
        showBackIcon: false,
        newHeaderHeight: true,
        shouldShowHeaderNavigation: true,
        hideBorder: true,
        showDialectSelector: true,
      },
      showFooter: true,
      mobileOnly: {
        showBottomNavigation: true,
        showSpecialAccessBanner: true,
      },
      userCulturePopup: {
        show: true,
      },
    },
    [PathNames.SETTINGS]: {
      showHeader: false,
      showTvSideNavigation: true,
      headerProps: {
        hideLogo: true,
        text: t("setting_page.title"),
        showBackIcon: true,
        showDialectSelector: false,
        isFocusable: false,
      },
      showFooter: false,
      mobileOnly: {
        showBottomNavigation: true,
      },
      userCulturePopup: {
        show: true,
      },
    },
    [PathNames.CHECKOUT]: {
      // TODO: fix checkout has its own header and footer
      showHeader: false,
      showFooter: false,
      userCulturePopup: {
        show: false,
      },
    },
    [PathNames.MOVIE_DETAIL]: {
      showHeader: false,
      showFooter: false,
      userCulturePopup: {
        show: false,
        allowAutoSelectedFromURL: true,
        showOnlyForLoggedInUser: true,
      },
      mobileOnly: {
        showBottomDownloadBanner: isSubscribed,
        showSpecialAccessBanner: true,
        showSpecialAccessDrawer: true,
      },
      showClaimTimer: true,
      pageName: PageNameEnum.CONTENT_DETAIL,
    },
    [PathNames.SHOW_DETAIL]: {
      showHeader: false,
      showFooter: false,
      userCulturePopup: {
        show: false,
        allowAutoSelectedFromURL: true,
        showOnlyForLoggedInUser: true,
      },
      mobileOnly: {
        showBottomDownloadBanner: isSubscribed,
        showSpecialAccessBanner: true,
        showSpecialAccessDrawer: true,
      },
      showClaimTimer: true,
      pageName: PageNameEnum.CONTENT_DETAIL,
    },
    [PathNames.MOVIE_CHARACTERS]: {
      showHeader: false,
      showFooter: false,
      mobileOnly: {
        showBottomDownloadBanner: false,
      },
    },
    [PathNames.SHOW_CHARACTERS]: {
      showHeader: false,
      showFooter: false,
      mobileOnly: {
        showBottomDownloadBanner: false,
      },
    },
    [PathNames.SHOW_WATCH]: {
      showHeader: false,
      showFooter: false,
      userCulturePopup: {
        show: false,
        allowAutoSelectedFromURL: true,
        showOnlyForLoggedInUser: true,
      },
      mobileOnly: {
        showSpecialAccessBanner:
          currentSpecialAccessData?.current_state_category ===
          ConsumptionStateCategoryEnum.Terminal,
      },
    },
    [PathNames.MOVIE_WATCH]: {
      showHeader: false,
      showFooter: false,
      userCulturePopup: {
        show: false,
        allowAutoSelectedFromURL: true,
        showOnlyForLoggedInUser: true,
      },
      mobileOnly: {
        showSpecialAccessBanner:
          currentSpecialAccessData?.current_state_category ===
          ConsumptionStateCategoryEnum.Terminal,
      },
    },
    [PathNames.TITLE]: {
      showHeader: false,
      showFooter: false,
      userCulturePopup: {
        show: false,
      },
      mobileOnly: {
        showBottomDownloadBanner: true,
        forceShowBottomDownloadBanner: true,
      },
    },
  };

  // Update state with culture values (both userCulture and tempCulture) from cookies if UserCulturePopup is enabled for the current route.
  useEffect(() => {
    if (currentRoute && config[currentRoute].userCulturePopup?.show) {
      const userCulture = getClientSideCookieValue(
        cookiesValues[CookiesEnum.UserCulture],
      );
      const tempCulture = getClientSideCookieValue(
        cookiesValues[CookiesEnum.TempCulture],
      );

      if (userCulture) {
        setUserCulture(userCulture);
      }
      if (tempCulture) {
        setTempCulture(tempCulture);
      }
    }
  }, [pathName, currentRoute]);

  useEffect(() => {
    // Use cached country cookie immediately if available, refresh in background
    const cachedCountry = getClientSideCookieValue(
      cookiesValues[CookiesEnum.UserCountry],
    );
    if (cachedCountry) {
      setIsInternational(isInternationalCountry(cachedCountry));
      // Refresh location in background without blocking
      fetchAndSetLocationInLocalStorage();
    } else {
      // No cache — must fetch before we know the country
      fetchAndSetLocationInLocalStorage().then((response) => {
        setIsInternational(isInternationalCountry(response.country));
      });
    }
  }, []);

  /**
   * Clear continue watching data when profile changes.
   * UserProvider should not depend on GlobalProvider to prevent circular dependencies.
   */
  useEffect(() => {
    const currentProfileId = selectedProfile?.profileId;
    if (
      currentProfileId &&
      previousProfileIdRef.current &&
      previousProfileIdRef.current !== currentProfileId
    ) {
      clearContinueWatchingData();
    }
    previousProfileIdRef.current = currentProfileId;
  }, [selectedProfile?.profileId, clearContinueWatchingData]);

  if (!currentRoute) return children;

  const shouldShowTvSideNavigation =
    isTvEnv() && config[currentRoute].showTvSideNavigation;

  const shouldShowStartTrialBanner =
    !isTvEnv() &&
    config[currentRoute].mobileOnly?.showStartTrialBanner &&
    !hasActiveAccess;

  const handleAppOpenDrawerOpenChange = () => {
    if (isAppOpenDrawerOpenRef.current) {
      isAppOpenDrawerOpenRef.current = false;
      setIsAppOpenDrawerOpen(false);
    }
  };

  const shouldShowSpecialAccessBanner =
    !isTvEnv() &&
    config[currentRoute].mobileOnly?.showSpecialAccessBanner &&
    isLoggedIn &&
    isSpecialAccessEnabled &&
    currentSpecialAccessData !== null &&
    totalConsumptionStates !== null &&
    subscriptionStatus === SUBSCRIPTION_STATUS.NOT_SUBSCRIBED;

  const shouldShowBottomNavigation =
    config[currentRoute].mobileOnly?.showBottomNavigation;

  const shouldShowSpecialAccessDrawer =
    !isTvEnv() &&
    isMobile &&
    config[currentRoute].mobileOnly?.showSpecialAccessDrawer &&
    isLoggedIn &&
    isSpecialAccessEnabled &&
    currentSpecialAccessData !== null &&
    subscriptionStatus === SUBSCRIPTION_STATUS.NOT_SUBSCRIBED;

  const shouldShowLocationChangePopup =
    !isTvEnv() &&
    isSubscribed &&
    !hasActiveAccess &&
    !hasShownLocationPopup &&
    (currentRoute === PathNames.COMMON_HOME || currentRoute === PathNames.HOME);

  const isHomeRoute =
    currentRoute === PathNames.COMMON_HOME || currentRoute === PathNames.HOME;

  return (
    <>
      <div
        className={`${
          config[currentRoute].headerProps?.isSticky
            ? isHomeRoute && config[currentRoute].headerProps?.hideBackground
              ? "sticky top-0 z-[1] md:fixed md:inset-x-0 md:top-0"
              : "sticky top-0 z-[1]"
            : ""
        } ${
          config[currentRoute].headerProps?.hideBackground && !isHomeRoute
            ? "md:-mb-28"
            : ""
        }`}
      >
        {config[currentRoute].showHeader && !isTvEnv() && (
          <Header
            {...config[currentRoute].headerProps}
            subscriptionStatus={subscriptionStatus}
          />
        )}
      </div>
      {shouldShowTvSideNavigation ? (
        <div
          className="relative min-h-screen transition-[margin] duration-200"
          style={{
            // We have used margin left for the main div when side nav is shown, as the side nav is present at the left of the main element but its expanded view overlays above the main layout.
            // We added padding to ensure the layout remains complete and used position fixed for the TV side nav component to achieve proper layering behavior.
            // On genre page, use CSS variable for dynamic margin based on nav expansion state
            marginLeft: shouldShowTvSideNavigation
              ? currentRoute === PathNames.GENRE
                ? "var(--tv-nav-width, " + TV_SIDE_NAVIGATION.MIN_WIDTH + ")"
                : TV_SIDE_NAVIGATION.MIN_WIDTH
              : "100%",
          }}
        >
          {children}
        </div>
      ) : (
        children
      )}
      {config[currentRoute].showFooter && (
        <div className="mt-auto hidden md:block">
          <HomeFooter transparentBg={config[currentRoute].transparentFooter} />
        </div>
      )}
      <div
        ref={mobileBottomStackRef}
        className={`sticky z-[15] block md:hidden ${
          shouldShowBottomNavigation ? "bottom-20" : "bottom-2"
        }`}
      >
        {shouldShowSpecialAccessBanner ? (
          <SpecialAccessBanner
            currentSpecialAccessData={currentSpecialAccessData}
            totalConsumptionStates={totalConsumptionStates}
            pathname={currentRoute}
          />
        ) : shouldShowStartTrialBanner ? (
          <StartTrialBanner
            subscriptionStatus={subscriptionStatus}
            lang={lang}
            dialect={dialect}
          />
        ) : null}
        {config[currentRoute].mobileOnly?.showBottomDownloadBanner && (
          <div className="mb-1.5 ">
            <AppDownloadBanner
              pageName={config[currentRoute].pageName}
              forceShow={
                config[currentRoute].mobileOnly?.forceShowBottomDownloadBanner
              }
            />
          </div>
        )}
        {shouldShowBottomNavigation && <BottomNavigation />}
      </div>

      {config[currentRoute].mobileOnly?.showAppOpenDrawer && (
        <AppOpenDrawer
          open={isAppOpenDrawerOpen}
          onDrawerOpenChange={handleAppOpenDrawerOpenChange}
          pageName={config[currentRoute].pageName}
          forceShow={config[currentRoute].mobileOnly?.showAppOpenDrawer}
          forceShowDesktop={false}
        />
      )}

      {config[currentRoute].userCulturePopup?.show &&
        !userCulture &&
        !isTvEnv() && (
          <UserCulturePopup
            tempCulture={tempCulture}
            onlyForLoggedInUser={
              config[currentRoute].userCulturePopup?.showOnlyForLoggedInUser
            }
            allowAutoSelectedFromURL={
              config[currentRoute].userCulturePopup?.allowAutoSelectedFromURL
            }
          />
        )}

      {shouldShowLocationChangePopup && (
        <LocationChangePopup onDismiss={() => setHasShownLocationPopup(true)} />
      )}

      {config[currentRoute].showClaimTimer &&
        !isInternational &&
        gamificationClaimTime &&
        paywallPageCookie &&
        (
          [
            PaywallPageCookieEnum.SPIN_THE_WHEEL,
            PaywallPageCookieEnum.HAMMER_CRACK,
          ] as string[]
        ).includes(paywallPageCookie) && (
          <div className="fixed bottom-8 right-8 z-50">
            <ExpiresInTimer
              expiresInSeconds={expiresInSeconds}
              variant="widget"
            />
          </div>
        )}
      {/* TV Side Navigation */}
      {shouldShowTvSideNavigation && <TvSideNavigation />}

      {/* Special Access Drawer - Mobile Only, Terminal State */}
      <SpecialAccessDrawer
        showSpecialAccessBanner={shouldShowSpecialAccessDrawer ?? false}
      />
    </>
  );
};
